import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  deleteSelaProject,
  createSelaProject,
  listSelaProjects,
  type SelaProjectCard,
  type StageKey,
  type StageVisual,
  type ActiveStageBadgeVariant,
} from '../services/selaApi';
import { useRumDwell } from '../utils/rum';
import { recordV2ErrorStage, recordV2FunnelStep, recordV2Latency } from '../utils/v2Rum';
import {
  buildCreateProjectRequestFromState,
  clearV2OnboardingState,
  readV2OnboardingState,
} from '../utils/v2Onboarding';
import { SelaProjectOnboardingFlow } from './sela-v2/SelaProjectOnboardingFlow';
import { SelaUserMenu } from './sela-v2/SelaUserMenu';

const STAGE_LABELS: Record<StageKey, string> = {
  siteEval: 'Site Eval',
  feasibility: 'Feasibility',
  design: 'Design',
  entitlement: 'Entitlement',
};

function ActiveStageBadge({ label, variant }: { label: string; variant: ActiveStageBadgeVariant }) {
  const styles: Record<ActiveStageBadgeVariant, string> = {
    blue: 'bg-[#dbebff] text-[#2570ee]',
    amber: 'bg-[#fcf1d1] text-[#b8870b]',
    green: 'bg-[#e0fae7] text-[#228b42]',
  };
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${styles[variant]}`}>
      {label}
    </span>
  );
}

function StagePill({ stageKey, visual }: { stageKey: StageKey; visual: StageVisual }) {
  const label = STAGE_LABELS[stageKey];
  if (visual === 'complete') {
    return (
      <span className="rounded-xl bg-[#e0fae7] px-2 py-0.5 text-[11px] font-medium text-[#228b42]">
        {label}
      </span>
    );
  }
  if (visual === 'current') {
    return (
      <span className="rounded-xl bg-[#dbebff] px-2 py-0.5 text-[11px] font-medium text-[#2570ee]">
        {label}
      </span>
    );
  }
  return (
    <span className="rounded-xl bg-[#f0f1f3] px-2 py-0.5 text-[11px] font-medium text-[#647488]">
      {label}
    </span>
  );
}

function MoreMenu({ children }: { children: (close: () => void) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        className="rounded-md border border-[#cdd5e1] bg-white px-2 py-1 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
      >
        ···
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-30 mt-1 min-w-[11rem] rounded-lg border border-[#d8e0eb] bg-white p-1 text-xs shadow-lg"
          role="menu"
          onClick={(event) => event.stopPropagation()}
        >
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}

function DeleteProjectModal({
  project,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  project: SelaProjectCard;
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl border border-[#d8e0eb] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-[#0f172a]">Delete project?</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#647488]">
              This will remove <span className="font-semibold text-[#0f172a]">{project.title}</span> from
              your project list. You can create a new project after deleting this one.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {error ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="rounded-lg border border-[#d8e0eb] px-4 py-2 text-sm font-semibold text-[#475669] hover:bg-slate-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
          >
            {deleting ? 'Deleting...' : 'Delete project'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  onDelete,
  onOpen,
}: {
  project: SelaProjectCard;
  onDelete: (project: SelaProjectCard) => void;
  onOpen: (projectId: string) => void;
}) {
  const stageOrder: StageKey[] = ['siteEval', 'feasibility', 'design', 'entitlement'];
  const isParcelMode = project.locationMode === 'parcel';
  return (
    <div className="relative flex min-w-0 flex-1 flex-col gap-4 rounded-xl border border-[#e1e8f0] bg-white p-5 text-left shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)] transition-shadow hover:border-[#cbd5e1] hover:shadow-md">
      <div className="absolute right-4 top-4 z-10">
        <MoreMenu>
          {(close) => (
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded-md px-3 py-2 text-left font-semibold text-red-700 hover:bg-red-50"
              onClick={() => {
                close();
                onDelete(project);
              }}
            >
              Delete project
            </button>
          )}
        </MoreMenu>
      </div>
      <Link
        to={`/project/${project.id}`}
        onClick={() => onOpen(project.id)}
        className="flex flex-1 flex-col gap-4 focus:outline-none focus:ring-2 focus:ring-[#2570ee] focus:ring-offset-2"
      >
        <div className="flex items-center justify-between gap-2 pr-10">
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-[#f0f1f3] px-2 py-0.5 text-[11px] font-medium text-[#475669]">
              {project.typeLabel}
            </span>
            {isParcelMode ? (
              <span className="rounded bg-[#fef3c7] px-2 py-0.5 text-[11px] font-medium text-[#92400e]">
                Parcel
              </span>
            ) : null}
          </div>
          <ActiveStageBadge label={project.activeStageLabel} variant={project.activeStageVariant} />
        </div>
        <h3 className="text-base font-semibold text-[#0f172a]">{project.title}</h3>
        <p className="text-[13px] text-[#647488]">{project.address}</p>
        <div className="h-px w-full bg-[#e1e8f0]" />
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#647488]">Stage progress</p>
        <div className="flex flex-wrap gap-1.5">
          {stageOrder.map((key) => (
            <StagePill key={key} stageKey={key} visual={project.stages[key]} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-[#647488]">
          <span className="font-medium text-[#475669]">{project.disciplines}</span>
          <span>{project.updated}</span>
        </div>
      </Link>
    </div>
  );
}

export default function SelaProjectsV2Page() {
  const location = useLocation();
  const navigate = useNavigate();
  useRumDwell('v2_project_list', { funnel_id: 'v2', funnel_step_index: 7 });
  const [projects, setProjects] = useState<SelaProjectCard[]>([]);
  const [canCreateProject, setCanCreateProject] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<SelaProjectCard | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [autoCreating, setAutoCreating] = useState(false);
  const [autoCreateError, setAutoCreateError] = useState<string | null>(null);
  const autoCreateStartedRef = useRef(false);
  const prefillParams = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const rawMode = (params.get('locationMode') || '').toLowerCase();
    const locationMode: 'address' | 'parcel' = rawMode === 'parcel' ? 'parcel' : 'address';
    return {
      initialPropertyAddress: params.get('address') || '',
      initialLocationMode: locationMode,
      initialParcelPin: params.get('parcelPin') || '',
      initialCity: params.get('city') || '',
      initialState: params.get('state') || '',
      isOnboarding: params.get('onboarding') === '1',
      autoCreate: params.get('autoCreate') === '1',
    };
  }, [location.search]);

  const loadProjects = useCallback(async () => {
    const startedAt = performance.now();
    setLoading(true);
    setError(null);
    recordV2FunnelStep('project_list_load', 'start');
    try {
      const result = await listSelaProjects();
      setProjects(result.projects);
      setCanCreateProject(result.canCreateProject);
      recordV2FunnelStep('project_list_load', 'success', {
        project_count: result.projects.length,
        can_create: result.canCreateProject,
      });
      recordV2FunnelStep('project_list_view', 'shown', {
        project_count: result.projects.length,
        can_create: result.canCreateProject,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects.');
      recordV2FunnelStep('project_list_load', 'failure');
      recordV2ErrorStage('project_list_load', err);
    } finally {
      recordV2Latency('project_list_load', performance.now() - startedAt);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!prefillParams.autoCreate || loading || autoCreateStartedRef.current) {
      return;
    }

    const storedState = readV2OnboardingState();
    if (!storedState?.pendingCreate) {
      return;
    }

    autoCreateStartedRef.current = true;
    const startedAt = performance.now();

    void (async () => {
      setAutoCreating(true);
      setAutoCreateError(null);
      recordV2FunnelStep('v2_onboarding_project_create', 'start', {
        location_mode: storedState.locationMode || 'address',
        deal_stage: storedState.dealStage,
        path_count: storedState.developmentPaths?.length || 0,
        priority_count: storedState.priorities?.length || 0,
        resume_after_auth: true,
      });

      try {
        if (!canCreateProject) {
          throw new Error('You already have an active project. Delete it before creating a new one.');
        }

        const payload = buildCreateProjectRequestFromState(storedState);
        if (!payload) {
          throw new Error('Missing project details from onboarding. Please start over.');
        }

        const project = await createSelaProject(payload);
        clearV2OnboardingState();
        recordV2FunnelStep('v2_onboarding_project_create', 'success', {
          project_id: project.id,
          location_mode: storedState.locationMode || 'address',
          deal_stage: storedState.dealStage,
          path_count: storedState.developmentPaths?.length || 0,
          resume_after_auth: true,
        });
        recordV2Latency('v2_onboarding_project_create', performance.now() - startedAt);
        recordV2FunnelStep('v2_onboarding_workspace_enter', 'success', {
          project_id: project.id,
          start_project_research: true,
        });
        navigate(`/project/${project.id}`, {
          replace: true,
          state: { startProjectResearch: true },
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create project.';
        setAutoCreateError(message);
        recordV2FunnelStep('v2_onboarding_project_create', 'failure', {
          location_mode: storedState.locationMode || 'address',
          deal_stage: storedState.dealStage,
          resume_after_auth: true,
        });
        recordV2ErrorStage('v2_onboarding_project_create', err);
      } finally {
        setAutoCreating(false);
      }
    })();
  }, [canCreateProject, loading, navigate, prefillParams.autoCreate]);

  const clearPrefillParams = () => {
    const params = new URLSearchParams(location.search || '');
    params.delete('address');
    params.delete('redirect');
    params.delete('onboarding');
    params.delete('autoCreate');
    params.delete('locationMode');
    params.delete('parcelPin');
    params.delete('city');
    params.delete('state');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  };

  const handleNewProjectClick = (source: 'header' | 'dashed_card') => {
    recordV2FunnelStep('new_project_click', 'click', { source });
    navigate('/?onboarding=1');
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    recordV2FunnelStep('project_delete_start', 'start', { project_id: projectToDelete.id });
    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteSelaProject(projectToDelete.id);
      setProjectToDelete(null);
      recordV2FunnelStep('project_delete_success', 'success', { project_id: projectToDelete.id });
      await loadProjects();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete project.');
      recordV2FunnelStep('project_delete_failure', 'failure', { project_id: projectToDelete.id });
      recordV2ErrorStage('project_delete', err, { project_id: projectToDelete.id });
    } finally {
      setDeleting(false);
    }
  };

  const count = projects.length;

  if (autoCreating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7fafd] px-4">
        <div className="max-w-md rounded-2xl border border-[#e1e8f0] bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-[#0f172a]">Creating your evaluation workspace</h1>
          <p className="mt-2 text-sm text-[#647488]">Setting up your parcel evaluation with the choices you made.</p>
        </div>
      </div>
    );
  }

  if (prefillParams.autoCreate && autoCreateError) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f7fafd]">
        <main className="mx-auto flex max-w-lg flex-1 flex-col justify-center px-6 py-12">
          <div className="rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
            <h1 className="text-lg font-semibold text-[#0f172a]">Could not create workspace</h1>
            <p className="mt-2 text-sm text-red-800">{autoCreateError}</p>
            <button
              type="button"
              onClick={() => {
                clearV2OnboardingState();
                navigate('/', { replace: true });
              }}
              className="mt-4 rounded-lg bg-[#101830] px-4 py-2 text-sm font-semibold text-white hover:bg-[#18233f]"
            >
              Start over
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (prefillParams.isOnboarding && canCreateProject) {
    return (
      <SelaProjectOnboardingFlow
        initialAddress={prefillParams.initialPropertyAddress}
        initialLocationMode={prefillParams.initialLocationMode}
        initialParcelPin={prefillParams.initialParcelPin}
        initialCity={prefillParams.initialCity}
        initialState={prefillParams.initialState}
        onExit={clearPrefillParams}
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f7fafd]">
      <header className="relative flex h-16 shrink-0 items-center border-b border-[#e1e8f0] bg-white px-6 md:px-8">
        <div className="flex items-center gap-2">
          <span className="text-xl font-semibold text-[#0f172a]">Sela</span>
          <span className="rounded bg-[#dbebff] px-1.5 py-0.5 text-[11px] font-medium text-[#2570ee]">
            predevelopment
          </span>
        </div>
        <nav
          aria-label="Primary"
          className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-7 text-sm md:flex"
        >
          <span className="font-medium text-[#0f172a]">Projects</span>
        </nav>
        <div className="ml-auto">
          <SelaUserMenu />
        </div>
      </header>

      <main className="flex flex-1 flex-col gap-6 p-6 md:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[28px] font-bold text-[#0f172a]">My Projects</h1>
            <p className="mt-1 text-sm text-[#475669]">
              {count} active predevelopment project{count === 1 ? '' : 's'}
            </p>
          </div>
          {canCreateProject ? (
            <button
              type="button"
              onClick={() => handleNewProjectClick('header')}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#2570ee] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1e5fd4] focus:outline-none focus:ring-2 focus:ring-[#2570ee] focus:ring-offset-2"
            >
              <span className="text-base font-medium leading-none">+</span>
              New Project
            </button>
          ) : null}
        </div>

        {!canCreateProject ? (
          <div className="rounded-lg border border-[#dbebff] bg-[#f5f9ff] px-4 py-3 text-sm text-[#1e4474]">
            One active project per account. Delete your existing project to start another.
          </div>
        ) : null}

        {prefillParams.isOnboarding && !canCreateProject ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            You already have an active project. Delete it before creating a new one.
          </div>
        ) : null}

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        ) : null}

        {loading ? (
          <div className="rounded-xl border border-[#e1e8f0] bg-white p-6 text-sm text-[#475669]">Loading projects...</div>
        ) : null}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onDelete={setProjectToDelete}
              onOpen={(projectId) => {
                recordV2FunnelStep('project_card_open', 'click', { project_id: projectId });
              }}
            />
          ))}
          {canCreateProject ? (
            <button
              type="button"
              onClick={() => handleNewProjectClick('dashed_card')}
              className="flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[#e1e8f0] bg-white px-6 text-center transition-colors hover:border-[#2570ee]/40 hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-[#2570ee] focus:ring-offset-2"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#dbebff] text-xl font-medium text-[#2570ee]">
                +
              </span>
              <span className="text-sm font-semibold text-[#2570ee]">Start a new project</span>
              <span className="max-w-[16rem] text-[13px] text-[#647488]">Add an address and define your stages</span>
            </button>
          ) : (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[#e1e8f0] bg-white px-6 text-center">
              <span className="max-w-[18rem] text-sm font-semibold text-[#475669]">
                One active project per account
              </span>
              <span className="max-w-[18rem] text-[13px] text-[#647488]">
                Delete your existing project to start another.
              </span>
            </div>
          )}
        </div>
      </main>

      {projectToDelete ? (
        <DeleteProjectModal
          project={projectToDelete}
          deleting={deleting}
          error={deleteError}
          onCancel={() => {
            if (!deleting) {
              setProjectToDelete(null);
              setDeleteError(null);
            }
          }}
          onConfirm={() => void handleDeleteProject()}
        />
      ) : null}
    </div>
  );
}
